import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Icon, { IconSizes, IconTypes } from '../../../components/foundations/Icon';
import Tooltip from '../../../components/molecules/Tooltip';
import ArtifactLoader from './ArtifactLoader';
import { formatKpiValue, formatKpiDelta } from './kpiFormat';
import { parseArtifactData } from './parseArtifactData';
import css from './KpiArtifact.css';

const DELTA_ICONS = { up: 'chevron-up', down: 'chevron-down', flat: null };

const KpiCard = ({ item }) => {
    const delta = formatKpiDelta(item.delta);
    const fullValue = formatKpiValue(item.value, item.format, item.unit, { compact: false });

    return (
        <div className={css.card}>
            <div className={css.label}>{item.label}</div>
            <Tooltip title={fullValue}>
                <div className={css.value}>{formatKpiValue(item.value, item.format, item.unit)}</div>
            </Tooltip>
            {(delta || item.comparison_label) && (
                <div className={css.footer}>
                    {delta && (
                        <span className={`${css.delta} ${css[delta.direction]}`}>
                            {DELTA_ICONS[delta.direction] && (
                                <Icon
                                    className={css.arrow}
                                    type={IconTypes.TABLER}
                                    name={DELTA_ICONS[delta.direction]}
                                    size={IconSizes.XX_SMALL}
                                    interactive={false}
                                />
                            )}
                            <span>{delta.text}</span>
                        </span>
                    )}
                    {item.comparison_label && <span className={css.comparison}>{item.comparison_label}</span>}
                </div>
            )}
        </div>
    );
};

KpiCard.propTypes = {
    item: PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        format: PropTypes.string,
        unit: PropTypes.string,
        delta: PropTypes.number,
        comparison_label: PropTypes.string,
    }).isRequired,
};

export const KpiArtifactInner = ({ data }) => {
    const parsed = useMemo(() => parseArtifactData(data), [data]);

    const items = Array.isArray(parsed?.items) ? parsed.items : [];

    if (!items.length) {
        return <div className={css.placeholder}>KPI data not available</div>;
    }

    return (
        <div className={css.cards}>
            {items.map((item, index) => (
                <KpiCard key={`${item.label}-${index}`} item={item} />
            ))}
        </div>
    );
};

KpiArtifactInner.propTypes = {
    data: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
};

const KpiArtifact = ({ artifact }) => (
    <ArtifactLoader artifact={artifact}>{data => <KpiArtifactInner data={data} />}</ArtifactLoader>
);

KpiArtifact.propTypes = {
    artifact: PropTypes.shape({
        data: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
        content_url: PropTypes.string,
    }).isRequired,
};

export default KpiArtifact;
